import { TableActionBox } from "@/components/molecule/tableActionBox/TableActionBox";
import { COMMON_TEXT } from "@/constants/text";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableOptions,
  useMantineReactTable,
} from "mantine-react-table";
import { useEffect, useState } from "react";

interface MantineSelectableTableProps<TData extends Record<string, any>>
  extends MRT_TableOptions<TData> {
  columns: MRT_ColumnDef<TData>[];
  data: TData[];
  onRowSelection?: (selectedRows: TData[]) => void; // 선택된 로우 변경 시 호출되는 핸들러
  onClickDelete?: (data: any) => void; // 삭제 버튼 클릭 시 호출되는 핸들러
  onClickCancel?: (data: any) => void; // 취소 버튼 클릭 시 호출되는 핸들러
  maxHeight?: string; // 테이블 최대 높이
  sorting?: MRT_SortingState;
  shouldClearSelection?: boolean; // 외부 트리거로 선택 초기화
}

export const MantineSelectableTable = <TData extends Record<string, any>>({
  columns,
  data,
  onRowSelection,
  maxHeight,
  sorting,
  shouldClearSelection,
  ...props
}: MantineSelectableTableProps<TData>) => {
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  useEffect(() => {
    if (onRowSelection) {
      const selectedRows = Object.keys(rowSelection)
        .filter((key) => rowSelection[key]) // 선택된 로우만 필터링
        .map((key) => data[Number(key)]);
      onRowSelection(selectedRows);
    }
  }, [rowSelection]);

  // 외부에서 선택 초기화 요청이 들어오면 체크박스 초기화
  useEffect(() => {
    if (shouldClearSelection) {
      setRowSelection({});
    }
  }, [shouldClearSelection]);

  const table = useMantineReactTable<TData>({
    columns,
    data: data,
    enableRowSelection: true,
    enableRowNumbers: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableMultiSort: true,
    enableTopToolbar: false,
    enablePagination: false,
    enableBottomToolbar: false,
    onRowSelectionChange: setRowSelection,
    enableRowActions: false,
    positionActionsColumn: "last",
    mantineTableContainerProps: { sx: { maxHeight: maxHeight ?? "15rem" } },
    mantineTableBodyRowProps: ({ row }) => ({
      //add onClick to row to select upon clicking anywhere in the row
      onClick: row.getToggleSelectedHandler(),
      sx: {
        cursor: "pointer",
        "&:hover": {
          opacity: 0.8,
        },
      },
    }),
    localization: {
      noRecordsToDisplay: COMMON_TEXT.DATA_EMPTY.NO_DATA,
    },
    renderRowActions: ({ row }) => {
      return <TableActionBox row={row} {...props} />;
    },
    state: {
      rowSelection,
      sorting: sorting ?? [],
    },
    ...props,
    defaultColumn: {
      size: 30,
    },
  });

  return <MantineReactTable table={table} />;
};
